import Anthropic from '@anthropic-ai/sdk';
import { buildReplyRequest, buildFeedbackRequest } from '../shared/prompts';

// Two ways to reach Claude:
//
//   trial    the browser POSTs to our proxy, which spends the owner's key. The
//            proxy builds the prompts itself from the scenario id, so all we
//            send is which scenario, who the partner is, and what was said.
//   own key  the browser talks to Anthropic directly and never touches our
//            server, so the prompt is built here.
//
// Both paths build from src/shared/prompts.js, so there is one set of prompts.

// Only the fields the prompts need. `kind` marks a silence the user let happen,
// which the coach reads as its own transcript line; `note` is UI-only.
const toTurns = (bubbles) =>
  bubbles.map(({ role, content, kind }) => (kind ? { role, content, kind } : { role, content }));

// POST to the trial proxy. Returns { ok, content } on success, or
// { ok: false, code, status } on failure, where `code` drives app-level
// handling ('owner_out_of_credits', 'trial_exhausted', 'turn_limit').
const postTrial = async (path, payload) => {
  let res;
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
  } catch (e) {
    return { ok: false, code: 'error', status: 'network' };
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    return { ok: false, code: body.code || 'error', status: res.status };
  }
  const data = await res.json();
  return { ok: true, content: data.content };
};

// Run a fully built request against the user's own key, from the browser.
const postDirect = async (apiKey, params) => {
  const client = new Anthropic({ apiKey, dangerouslyAllowBrowser: true });
  const message = await client.messages.create(params);
  console.log('[anthropic] usage:', message.usage);
  return { ok: true, content: message.content };
};

const hasCredentials = (auth) => !!(auth && (auth.trial || auth.apiKey));

const failure = (error) => {
  if (error.status === 401) {
    return {
      success: false,
      code: 'auth',
      error: 'Authentication failed. Please check your API key format and validity.',
    };
  }
  return { success: false, code: 'error', error: error.message, status: error.status || 'unknown' };
};

// Send a turn to Claude and get back the other person's in-character reply plus
// their auto-detected mood — both from a single structured-output call.
export const sendStructuredMessage = async (
  auth,
  scenario,
  character,
  bubbles,
  { conversationId, pressure = false, windDown = false } = {}
) => {
  if (!hasCredentials(auth)) {
    return { success: false, code: 'error', error: 'No credentials provided' };
  }

  try {
    const result = auth.trial
      ? await postTrial('/api/trial/reply', {
          conversationId,
          scenarioId: scenario.id,
          character,
          turns: toTurns(bubbles),
          pressure,
          windDown,
        })
      : await postDirect(auth.apiKey, buildReplyRequest(scenario, character, bubbles, { pressure, windDown }));

    if (!result.ok) {
      return { success: false, code: result.code, status: result.status };
    }
    const parsed = JSON.parse(result.content[0].text);
    return {
      success: true,
      reply: parsed.reply,
      mood: parsed.mood,
      ended: parsed.status === 'ended',
      endReason: parsed.endReason || '',
      outcome: parsed.outcome || 'ongoing',
    };
  } catch (error) {
    console.error('Error calling Anthropic API:', error);
    return failure(error);
  }
};

// Generate coaching feedback on how the user handled the scenario conversation.
// `conclusion` is null when the user ended the chat by hand.
export const generateFeedback = async (
  auth,
  scenario,
  character,
  conversation,
  { conversationId, conclusion = null } = {}
) => {
  if (!hasCredentials(auth)) {
    return { success: false, code: 'error', error: 'No credentials provided' };
  }

  try {
    const result = auth.trial
      ? await postTrial('/api/trial/feedback', {
          conversationId,
          scenarioId: scenario.id,
          character,
          turns: toTurns(conversation),
          // The rest of `conclusion` is the transcript, which is already in `turns`.
          conclusion: conclusion && { endReason: conclusion.endReason, outcome: conclusion.outcome },
        })
      : await postDirect(auth.apiKey, buildFeedbackRequest(scenario, character, conversation, conclusion));

    if (!result.ok) {
      return { success: false, code: result.code, status: result.status };
    }
    return { success: true, feedback: result.content[0].text };
  } catch (error) {
    console.error('Error generating feedback:', error);
    return failure(error);
  }
};
